import { emitUIEvent } from '@montugroup/data-collection';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Skeleton, Typography } from '@mui/material';
import type { AccordionProps as MUIAccordionProps } from '@mui/material/Accordion';
import AccordionMUI from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import type { FC, ReactNode, SyntheticEvent } from 'react';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

const AccordionStyled = styled(AccordionMUI)(({ theme }) => ({
  '&.Mui-expanded': {
    borderBottom: `1px solid ${theme.palette.grey[800]}`,
  },
  '&:last-of-type': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const SummaryStyled = styled(AccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'withoutPadding',
})<{ withoutPadding?: boolean }>(({ theme, withoutPadding }) => ({
  borderBottom: `1px solid ${theme.palette.grey[800]}`,
  ...(withoutPadding
    ? {
        paddingLeft: 0,
        paddingRight: 0,
      }
    : {}),
}));

const DetailsStyled = styled(AccordionDetails, {
  shouldForwardProp: (prop) => prop !== 'withoutPadding',
})<{ withoutPadding?: boolean }>(({ withoutPadding }) => ({
  ...(withoutPadding
    ? {
        paddingLeft: 0,
        paddingRight: 0,
      }
    : {}),
}));

type LoadingAccordionProps = {
  title?: string;
  item?: ReactNode;
  isLoading: true;
};

type LoadedAccordionProps = {
  title: string;
  item: ReactNode;
  isLoading?: false;
};

type AccordionProps = (LoadingAccordionProps | LoadedAccordionProps) & {
  withoutPadding?: boolean;
  trackingContextId?: string;
  id: string;
} & Pick<MUIAccordionProps, 'defaultExpanded'>;

export const Accordion: FC<AccordionProps> = ({
  isLoading,
  item,
  title,
  defaultExpanded,
  withoutPadding,
  trackingContextId,
  id,
}) => {
  const getCompoundTestId = getCompoundTestIdGenerator('accordion', id);

  const handleAccordionChange = (_event: SyntheticEvent, isExpanded: boolean) => {
    emitUIEvent(
      {
        component_name: 'Accordion',
        event_name: isExpanded ? 'onExpand' : 'onCollapse',
        id,
        value: title,
        context_id: trackingContextId,
      },
      [],
    );
  };

  let titleComponent = (
    <Skeleton
      data-testid={getCompoundTestId('loadingTitle')}
      variant="rounded"
      sx={({ spacing }) => ({
        width: spacing(12),
      })}
    />
  );

  let productDetailsComponent: ReactNode | Element[] = new Array(3)
    .fill(null)
    .map((_, i) => (
      <Skeleton data-testid={getCompoundTestId(`loadingDetails-${i}`)} key={`accordion-loading-details-${id}-${i}`} />
    ));

  if (!isLoading) {
    titleComponent = <Typography fontWeight="bold">{title}</Typography>;
    productDetailsComponent = item;
  }

  return (
    <AccordionStyled
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      onChange={handleAccordionChange}
      data-testid={getCompoundTestId()}
    >
      <SummaryStyled
        expandIcon={<ExpandMore />}
        aria-controls={getCompoundTestId('summary')}
        id={getCompoundTestId('summary')}
        withoutPadding={withoutPadding}
      >
        {titleComponent}
      </SummaryStyled>
      <DetailsStyled data-testid={getCompoundTestId('details')} withoutPadding={withoutPadding}>
        {productDetailsComponent}
      </DetailsStyled>
    </AccordionStyled>
  );
};
